/** @format */
import {
	Component,
	computed,
	EventEmitter,
	inject,
	Inject,
	Input,
	OnDestroy,
	OnInit,
	Output,
	Signal,
	ViewChild
} from '@angular/core';
import { filter, map, Subject, take, takeUntil } from 'rxjs';
import { AnalyticsService, BreakpointsService, WINDOW } from '@shared/data-access/services';
import { BreakPointNew } from '@shared/data-access/enums';
import { Store } from '@ngrx/store';
import {
	HmfButtonComponent,
	HmfDrawerComponent,
	HmfSelectorComponent
} from '@dsg-tech/homefield-angular';
import { CommonModule } from '@angular/common';
import {
	homefieldIconAlertInfoFilled,
	HomefieldIconLibraryModule,
	homefieldIconPenEdit,
	HomefieldIconsLibraryService
} from '@dsg-tech/homefield-angular-icons';
import { SelectorAttributesComponent } from '../selector-attributes/selector-attributes.component';
import { AddToCartButtonComponent } from '@pdp/add-to-cart/components';
import {
	AttributeOption,
	selectAttributes,
	selectErrorList,
	selectSelectedAttributeOptions
} from '@pdp/attributes/data-access';
import { Actions, ofType } from '@ngrx/effects';
import { PriceComponent } from '@pdp/components';
import {
	AddToCartButtonService,
	atcAttemptedWithAttributeErrors,
	warrantyATCAttemptedWithAttributeErrors
} from '@pdp/add-to-cart/data-access';
import { selectResolvedSku } from '@pdp/data-access/state';
import { ProductSelectionService } from '@pdp/data-access/services';

@Component({
	// eslint-disable-next-line @angular-eslint/component-selector
	selector: 'pdp-attributes-components-config-attributes-panel',
	templateUrl: './config-attributes-panel.component.html',
	styleUrl: './config-attributes-panel.component.scss',
	imports: [
		CommonModule,
		HmfButtonComponent,
		HmfDrawerComponent,
		HmfSelectorComponent,
		HomefieldIconLibraryModule,
		SelectorAttributesComponent,
		AddToCartButtonComponent,
		PriceComponent
	],
	providers: [ProductSelectionService]
})
export class ConfigAttributesPanelComponent implements OnInit, OnDestroy {
	defaultClubAttributes = {
		flex: { name: 'Flex', value: 'Regular Flex' },
		hand: { name: 'Hand', value: 'Right Hand' },
		loft: { name: 'Loft', value: '10.5°' }
	};

	attributes$ = this.store.select(selectAttributes);
	selectedAttributes$ = this.store.select(selectSelectedAttributeOptions);
	resolvedSku$ = this.store.select(selectResolvedSku);
	selectErrorList$ = this.store.select(selectErrorList);

	@ViewChild(HmfDrawerComponent) drawerComponent: HmfDrawerComponent | undefined;

	@Input() customSeoUrl = '';
	@Output() attributesSelected = new EventEmitter();

	private readonly breakpointsService = inject(BreakpointsService);

	/**
	 * @description this is a map of observables we use to
	 * individually track the selected attribute option per product attribute
	 */
	clubAttributeMap; //this needs to be undefined at first

	//ecode level product data
	product = this.productSelectionService.getProductStyleData();

	analyticsService: AnalyticsService;
	window: Window;

	drawerDirection: Signal<'right' | 'bottom'> = computed(() => {
		const breakpoint = this.breakpointsService.breakpoint();
		return breakpoint <= BreakPointNew.SMALL ? 'bottom' : 'right';
	});

	private destroy$ = new Subject<void>();

	constructor(
		@Inject(WINDOW) window: Window,
		analyticsService: AnalyticsService,
		private addToCartButtonService: AddToCartButtonService,
		private actions$: Actions,
		private store: Store,
		private readonly homefieldIconsLibraryService: HomefieldIconsLibraryService,
		private readonly productSelectionService: ProductSelectionService
	) {
		this.homefieldIconsLibraryService.registerIcons([
			homefieldIconAlertInfoFilled,
			homefieldIconPenEdit
		]);

		this.analyticsService = analyticsService;
		this.window = window;
	}

	ngOnInit(): void {
		const initialAttributes = [];
		this.clubAttributeMap = {};

		this.actions$
			.pipe(
				ofType(atcAttemptedWithAttributeErrors, warrantyATCAttemptedWithAttributeErrors),
				takeUntil(this.destroy$)
			)
			.subscribe(() => {
				this.openDrawer();
			});

		this.attributes$
			.pipe(
				filter((attributes) => attributes && attributes.length > 0),
				take(1)
			)
			.subscribe((attributes) => {
				attributes.forEach((attribute) => {
					attribute.some((option) => {
						if (
							option.value ===
							this.defaultClubAttributes[option.name.toLowerCase()]?.value
						) {
							initialAttributes.push(option);
							return true;
						}
					});

					this.addAttributeToMap(attribute);
				});

				this.markSelectedAttributes(initialAttributes);
			});

		this.addToCartButtonService.addToCartClick$.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.closeDrawer();
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	openDrawer() {
		this.drawerComponent?.openDrawer();
	}

	closeDrawer() {
		this.drawerComponent?.closeDrawer();
	}

	registerAnalyticsAndRedirect() {
		// Set the Analytics event type
		this.analyticsService.setType('event', 'CustomizeThisClubButtonClicked');

		// Trigger the Analytics update
		this.analyticsService.triggerAnalyticsEvent('reporting:update');

		//navigate to seoLink
		this.window.location = this.customSeoUrl;
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	trackByFn(index: number): number {
		return index;
	}

	private markSelectedAttributes(attributes: AttributeOption[] | AttributeOption) {
		if (Array.isArray(attributes)) {
			if (attributes.length > 0) {
				this.attributesSelected.emit(attributes);
			}
		} else {
			this.attributesSelected.emit([attributes]);
		}
	}

	/**
	 * @description We create an observable for each attribute that we can subscribe to
	 */
	private addAttributeToMap(attribute: AttributeOption[]) {
		this.clubAttributeMap[attribute[0].name.toLowerCase()] = this.selectedAttributes$.pipe(
			filter((selectedAttributes) => selectedAttributes && selectedAttributes.length > 0),
			map((selectedAttributes) =>
				selectedAttributes.find((attr) => attr.name === attribute[0].name)
			)
		);
	}
}
